import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { MeResponse, ReturnRow } from "../types";

/** Раздел «Возвраты» (Блок 6 ТЗ). Список по статусам + быстрая
 *  разноска (приём → выбор резолюции). Полная форма резолюции — отдельный
 *  модал; здесь — листинг с переходом в детали. */

const STATUS_LABEL: Record<ReturnRow["status"], string> = {
  expecting: "⏳ Ожидаем",
  received: "📥 Пришёл",
  resolved: "✅ Закрыт",
};

const SOURCE_LABEL: Record<ReturnRow["source"], string> = {
  exporter: "экспортёр",
  company_payer: "наша компания",
  subcontractor: "субподрядчик",
  fx_provider: "FX-провайдер",
};

function fmt(n: number): string {
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

export function ReturnsPage({ me }: { me: MeResponse }) {
  const [tab, setTab] = useState<ReturnRow["status"] | "all">("expecting");
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [active, setActive] = useState<ReturnRow | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = tab === "all" ? "" : `?status=${tab}`;
    api
      .get<{ returns: ReturnRow[] }>(`/api/returns${q}`)
      .then((r) => setRows(r.returns))
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tab, reload]);

  if (!me.permissions.returns_view) {
    return (
      <div className="text-tg-hint text-sm">Раздел доступен админу, финруку и бухгалтеру.</div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">↩️ Возвраты</h1>

      <div className="flex gap-1 mb-3 text-xs">
        {(["expecting", "received", "resolved", "all"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={
              "px-3 py-1.5 rounded-lg " +
              (tab === s ? "bg-tg-button text-tg-buttonText font-medium" : "bg-tg-sectionBg")
            }
          >
            {s === "all" ? "Все" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {loading && <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>}

      <div className="space-y-2">
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => setActive(r)}
            className="w-full text-left bg-tg-sectionBg rounded-xl p-3"
          >
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">
                #{r.id} · заявка №{r.deal_id}
              </div>
              <span className="text-xs text-tg-hint">{STATUS_LABEL[r.status]}</span>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {SOURCE_LABEL[r.source]} · ожидаем <b>{fmt(r.expected_amount)} {r.currency}</b>
              {r.received_amount != null && (
                <> · пришло <b>{fmt(r.received_amount)}</b></>
              )}
            </div>
            {r.resolution !== "none" && (
              <div className="text-xs mt-1">
                Решение:{" "}
                <b>
                  {r.resolution === "repeat" ? "повторная оплата" : "возврат RUB"}
                </b>
                {r.financial_result_rub != null && (
                  <> · фин.рез.{" "}
                    <span
                      className={
                        r.financial_result_rub >= 0
                          ? "text-emerald-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {fmt(r.financial_result_rub)} ₽
                    </span>
                  </>
                )}
              </div>
            )}
          </button>
        ))}
        {!loading && rows.length === 0 && (
          <div className="text-tg-hint text-sm">Записей нет.</div>
        )}
      </div>

      {active && (
        <ReturnModal
          ret={active}
          canResolve={me.permissions.returns_resolve}
          onClose={() => setActive(null)}
          onChanged={() => {
            setActive(null);
            setReload((x) => x + 1);
          }}
        />
      )}
    </div>
  );
}

function ReturnModal({
  ret, canResolve, onClose, onChanged,
}: {
  ret: ReturnRow;
  canResolve: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [view, setView] = useState<"main" | "repeat" | "rub">("main");
  const [received, setReceived] = useState(String(ret.received_amount ?? ret.expected_amount));
  const [feePct, setFeePct] = useState("");
  const [feeFixed, setFeeFixed] = useState("");
  const [refundRate, setRefundRate] = useState("");
  const [refundIncludeFee, setRefundIncludeFee] = useState(false);
  const [finResult, setFinResult] = useState("");
  const [reason, setReason] = useState("");
  const [fault, setFault] = useState<"executor" | "client" | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doReceive = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/returns/${ret.id}/receive`, {
        received_amount: Number(received),
      });
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const doRepeat = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/returns/${ret.id}/resolve/repeat`, {
        fee_percent: feePct ? Number(feePct) : null,
        fee_fixed: feeFixed ? Number(feeFixed) : null,
      });
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const doRub = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/returns/${ret.id}/resolve/rub_refund`, {
        rate: Number(refundRate),
        include_fee: refundIncludeFee,
        financial_result_rub: finResult ? Number(finResult) : null,
        fault: fault || null,
        reason: reason || null,
      });
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-30"
      onClick={onClose}
    >
      <div
        className="bg-tg-bg rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg p-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Возврат #{ret.id} · заявка №{ret.deal_id}</h2>
          <button onClick={onClose} className="text-tg-hint text-xl px-2">×</button>
        </div>

        <div className="text-sm text-tg-hint mb-3">
          {SOURCE_LABEL[ret.source]} · ожидаем <b>{fmt(ret.expected_amount)} {ret.currency}</b>
          {ret.received_amount != null && <> · пришло <b>{fmt(ret.received_amount)}</b></>}
        </div>

        {err && (
          <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">{err}</div>
        )}

        {view === "main" && (
          <>
            {ret.status === "expecting" && (
              <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
                <div className="text-sm font-medium mb-2">Подтвердить получение</div>
                <div className="flex gap-2">
                  <input
                    inputMode="decimal"
                    className="flex-1 px-3 py-2 rounded-lg bg-tg-bg border border-tg-secondaryBg text-sm"
                    value={received}
                    onChange={(e) => setReceived(e.target.value)}
                    placeholder={`Сколько пришло, ${ret.currency}`}
                  />
                  <button
                    disabled={busy || !received}
                    onClick={doReceive}
                    className="px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
                  >
                    Принять
                  </button>
                </div>
              </section>
            )}

            {canResolve && ret.status !== "resolved" && (
              <section className="bg-tg-sectionBg rounded-xl p-3 space-y-2">
                <div className="text-sm font-medium">Резолюция</div>
                <button
                  onClick={() => setView("repeat")}
                  className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm text-left"
                >
                  🔁 Повторная оплата
                </button>
                <button
                  onClick={() => setView("rub")}
                  className="w-full px-3 py-2 rounded-lg bg-tg-bg text-sm text-left"
                >
                  💰 Возврат клиенту в рублях
                </button>
              </section>
            )}

            {ret.status === "resolved" && (
              <section className="bg-tg-sectionBg rounded-xl p-3 text-sm">
                Резолюция:{" "}
                <b>{ret.resolution === "repeat" ? "повторная оплата" : "возврат RUB"}</b>
                {ret.financial_result_rub != null && (
                  <div className="mt-1">
                    Финрезультат: <b>{fmt(ret.financial_result_rub)} ₽</b>
                  </div>
                )}
                {ret.reason && <div className="mt-1 text-tg-hint">Причина: {ret.reason}</div>}
              </section>
            )}
          </>
        )}

        {view === "repeat" && (
          <section className="space-y-3">
            <div className="text-sm font-medium">Повторная оплата</div>
            <div>
              <label className="text-xs text-tg-hint">Доп. комиссия (% от суммы)</label>
              <input
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                value={feePct}
                onChange={(e) => setFeePct(e.target.value)}
                placeholder="0.5"
              />
            </div>
            <div>
              <label className="text-xs text-tg-hint">… или фикс. сумма</label>
              <input
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                value={feeFixed}
                onChange={(e) => setFeeFixed(e.target.value)}
                placeholder="500"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setView("main")} className="flex-1 px-3 py-2 rounded-lg bg-tg-sectionBg text-sm">Назад</button>
              <button
                disabled={busy}
                onClick={doRepeat}
                className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
              >
                Применить
              </button>
            </div>
          </section>
        )}

        {view === "rub" && (
          <section className="space-y-3">
            <div className="text-sm font-medium">Возврат клиенту в рублях</div>
            <div>
              <label className="text-xs text-tg-hint">Курс возврата</label>
              <input
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                value={refundRate}
                onChange={(e) => setRefundRate(e.target.value)}
                placeholder="99.5"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={refundIncludeFee} onChange={(e) => setRefundIncludeFee(e.target.checked)} />
              Вернуть в т.ч. комиссию
            </label>
            <div>
              <label className="text-xs text-tg-hint">Фин. результат, ₽ (опционально)</label>
              <input
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                value={finResult}
                onChange={(e) => setFinResult(e.target.value)}
                placeholder="-500"
              />
            </div>
            <div>
              <label className="text-xs text-tg-hint">По чьей вине</label>
              <div className="flex gap-2 mt-1">
                {(["executor", "client"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFault(f)}
                    className={
                      "flex-1 px-3 py-2 rounded-lg text-sm " +
                      (fault === f
                        ? "bg-tg-button text-tg-buttonText font-medium"
                        : "bg-tg-sectionBg")
                    }
                  >
                    {f === "executor" ? "Исполнитель" : "Клиент"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-tg-hint">Причина (опционально)</label>
              <input
                className="w-full px-3 py-2 rounded-lg bg-tg-sectionBg border border-tg-secondaryBg text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="неверные реквизиты получателя"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setView("main")} className="flex-1 px-3 py-2 rounded-lg bg-tg-sectionBg text-sm">Назад</button>
              <button
                disabled={busy || !refundRate}
                onClick={doRub}
                className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
              >
                Применить
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
